import { CellAgent, getTableCellFromApi } from '@/@types/Table';
import { createCellKey } from '@/store/reducers/TableWidgetStore';
import { getChatAPI } from '@/api/ChatApi';
import { getExistingColumnCellValues } from '@/store/Selectors/GetExistingColumnCellValues';
import { getTableAPI } from '@/api/TableApi';
import { getTableRecommendationsStateForThread } from '@/store/reducers/TableRecommendationsStore';
import { NoraToolNames } from '@/utils/RegenerateColumn';
import { RootState } from '@/store/store';
import { SendMessageResponseBody } from '@/api/ApiResponse';
import { TableColumn, TableRow } from '@/models/Table';
import { ThreadId } from '@/models/Thread';

import { createAsyncThunk } from '@reduxjs/toolkit';

export interface PopulateTableCellsPayload {
  currentThreadId: ThreadId;
  cellValues: Record<string, RawCellValue>;
}

export type PopulateTableCellsArgs = {
  threadId: ThreadId;
  columns?: TableColumn[];
  rows?: TableRow[];
};

export type PopulateTableCellsError = {
  errorMessage: string;
};

export type RawCellValueWithValue = {
  value: string;
  metadata?: { evidenceId?: number };
};

export type RawCellValue = RawCellValueWithValue | null | { error: string };

export const populateTableCellsData = createAsyncThunk<
  PopulateTableCellsPayload,
  PopulateTableCellsArgs,
  { state: RootState; rejectValue: PopulateTableCellsError }
>(
  'data/populateTableCellsData',
  async ({ threadId, columns, rows }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const tableData = getTableRecommendationsStateForThread(state, threadId);
      const tableColumns = columns ?? tableData?.columns;
      const tableRows = rows ?? tableData?.rows;
      if (!tableColumns?.length || !tableRows?.length) {
        return rejectWithValue({ errorMessage: 'Missing rows and columns' });
      }

      // fetching existing values per column to include in the prompt
      // map of columnId to list of existing values for that column
      const existingCellValues = getExistingColumnCellValues(
        state,
        new Set(tableColumns.map((col) => col.id)),
      );
      const cellsJson: Record<string, RawCellValue> = {};
      // make a separate adhoc request for each column
      const adhocReqs = tableColumns.map((column) => {
        // set initial value of row-col cell to null so if the adhoc fails, the cell will be null and we can render an error
        // eslint-disable-next-line array-callback-return
        tableRows.map((row) => {
          cellsJson[createCellKey(row.id, column.id)] = null;
        });
        if (column.tools.includes(NoraToolNames.CELL_VALUE_GENERATION)) {
          return makeAgentReq(column, tableRows, existingCellValues, cellsJson);
        } else {
          return makeAdhocReq(column, tableRows, existingCellValues, cellsJson);
        }
      });

      await Promise.all(adhocReqs);

      return {
        currentThreadId: threadId,
        cellValues: cellsJson,
      };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue({ errorMessage: error.message });
      }

      throw error;
    }
  },
);

const makeAdhocReq = async (
  column: TableColumn,
  tableRows: TableRow[],
  existingCellValues: Record<string, string[]>,
  cellsJson: Record<string, RawCellValue>,
) => {
  const chatApi = getChatAPI();
  const prompt = buildTablePrompt(
    column,
    tableRows,
    existingCellValues[column.id],
  );

  const rowCorpusIdToIdMap: Record<string, string> = tableRows.reduce(
    (acc, row) => {
      if (!row.paperCorpusId) {
        return acc;
      }
      acc[row.paperCorpusId.toString()] = row.id;
      return acc;
    },
    {} as Record<string, string>,
  );

  return chatApi
    .sendAdhocMessage({
      text: prompt,
    })
    .then((resp) => {
      const rowValues = parseJsonFromAdhocResp(resp.data);
      for (const val of rowValues) {
        const rowCorpusId = val.corpusId;
        const value = val.value;
        const cellVal: RawCellValueWithValue = { value };
        const evidenceId = val.metadata?.evidenceId;
        if (evidenceId) {
          cellVal.metadata = { evidenceId };
        }
        cellsJson[createCellKey(rowCorpusIdToIdMap[rowCorpusId], column.id)] =
          cellVal;
      }
    })
    .catch((error) => {
      console.error('Error in populateTableCellsData', error, prompt);
      // eslint-disable-next-line array-callback-return
      tableRows.map((row) => {
        const cellKey = createCellKey(row.id, column.id);
        cellsJson[cellKey] = { error: error.message };
      });
    });
};
const makeAgentReq = async (
  column: TableColumn,
  tableRows: TableRow[],
  existingCellValues: Record<string, string[]>,
  cellsJson: Record<string, RawCellValue>,
) => {
  const tableApi = getTableAPI();
  const payload = {
    column_name: column.name,
    column_def: column.description,
    is_metadata: column.isMetadata ?? false,
    ui_source_type: 'ROWZ',
    corpus_ids: tableRows.reduce((corpusIds: string[], row: TableRow) => {
      if (row.paperCorpusId) {
        corpusIds.push(`${row.paperCorpusId}`);
      }
      return corpusIds;
    }, []),
    current_values: existingCellValues[column.id],
  };

  const rowCorpusIdToIdMap: Record<string, string> = tableRows.reduce(
    (acc, row) => {
      if (row.paperCorpusId) {
        acc[`${row.paperCorpusId}`] = row.id;
      }
      return acc;
    },
    {} as Record<string, string>,
  );
  return tableApi
    .getValuesForColumn(payload)
    .then((cells) => {
      for (const cell of cells.data) {
        const typedCell: CellAgent = getTableCellFromApi(cell);
        const rowCorpusId = typedCell.corpusId;
        const value = typedCell.displayValue;
        const cellVal: RawCellValueWithValue = { value };
        const evidenceId = typedCell.metadata?.evidenceId;
        if (evidenceId) {
          cellVal.metadata = { evidenceId };
        }
        cellsJson[createCellKey(rowCorpusIdToIdMap[rowCorpusId], column.id)] =
          cellVal;
      }
    })
    .catch((error) => {
      console.error('Error in populateTableCellsData', error, payload);
      // eslint-disable-next-line array-callback-return
      tableRows.map((row) => {
        const cellKey = createCellKey(row.id, column.id);
        cellsJson[cellKey] = {
          error: 'unable to fetch cell value from cell task agent',
        };
      });
    });
};

export const buildTablePrompt = (
  column: TableColumn,
  rows: TableRow[],
  existingValues?: string[],
): string => {
  return `Help me figure out some of the cell values in my paper comparison table. Specifically, I need to figure out the value for a set of corpusIds for a column named "${
    column.name
  }". The detailed description for the column is "${column.description}".
    ${
      existingValues
        ? `Some examples of the existing cell values are ${existingValues.join(
            ', ',
          )}.`
        : ''
    } Use the following tools: ${JSON.stringify(
    column.tools,
  )} to figure out the cell values. List of corpusId to be filled: ${JSON.stringify(
    rows.map((row) => row.paperCorpusId),
  )}.
    You MUST return the output in the valid JSON format so I can parse it in Python. The format is list of JSON object with the following keys "corpusIds", "value", and optionally "metadata". The value field is a single string that contains a concise natural language description to be displayed in a table cell, and include an additional metadata object if the tool that was called included additional metadata about the cell.
    For example:
    [
      {corpusId: 12345, value: "Subjective video quality scores via crowdsourcing", metadata: {evidenceId: 789}},
      {corpusId: 5432, value: "Object video quality scores", metadata: {evidenceId: 791}},
      ...
    ]
  `;
};

interface AdhocResponseCell {
  corpusId: number;
  value: string;
  metadata?: { evidenceId?: number };
}

// returns a list of AdhocResponseCell records, given the llm formats it as requested in the prompt
// Adhoc response format is "str description ```json(a json string)```"
// Using a regex to extract all characters between ```json and ```
const parseJsonFromAdhocResp = (
  data: SendMessageResponseBody,
): AdhocResponseCell[] => {
  const regex = /```json([\s\S]*?)```/;
  const jsonStr = data.llm_output?.match(regex);

  return jsonStr ? JSON.parse(jsonStr[1]) : [];
};
